import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useLayout, getDensityClasses } from '../contexts/LayoutContext';
import { AssessmentValidators, DataSanitizers, validateAndSanitize } from '../utils/validation';

interface Goal {
  id: string;
  text: string;
  priority: 'high' | 'medium' | 'low';
}

interface ClientGoalsSectionProps {
  goals?: Goal[];
  onGoalsChange: (goals: Goal[]) => void;
  isEditable?: boolean;
}

const ClientGoalsSection: React.FC<ClientGoalsSectionProps> = ({
  goals,
  onGoalsChange,
  isEditable = true
}) => {
  const { density } = useLayout();
  const densityClasses = getDensityClasses(density);
  const [newGoal, setNewGoal] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Ensure goals is always an array to prevent runtime errors
  const safeGoals = Array.isArray(goals) ? goals : [];

  // Validate goals data integrity
  useEffect(() => {
    if (goals && Array.isArray(goals)) {
      const { result } = validateAndSanitize(goals, AssessmentValidators.assessmentGoals);
      if (!result.isValid) {
        console.warn('Goals data validation failed:', result.errors);
        // In a real app, you might want to reset or fix the data
      }
    }
  }, [goals]);

  const addGoal = () => {
    if (newGoal.trim()) {
      // Validate and sanitize the goal text
      const { result, sanitizedValue } = validateAndSanitize(
        newGoal.trim(),
        AssessmentValidators.clientName,
        DataSanitizers.trimString
      );

      if (result.isValid) {
        const goal: Goal = {
          id: Date.now().toString(),
          text: sanitizedValue,
          priority: 'medium'
        };
        onGoalsChange([...safeGoals, goal]);
        setNewGoal('');
      } else {
        // In a real app, you might want to show these errors to the user
        console.warn('Goal validation failed:', result.errors);
      }
    }
  };

  const removeGoal = (id: string) => {
    onGoalsChange(safeGoals.filter(goal => goal.id !== id));
  };

  const updateGoalPriority = (id: string, priority: 'high' | 'medium' | 'low') => {
    onGoalsChange(safeGoals.map(goal => 
      goal.id === id ? { ...goal, priority } : goal
    ));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-error-100 text-error-800 border-error-200';
      case 'medium': return 'bg-warning-100 text-warning-800 border-warning-200';
      case 'low': return 'bg-success-100 text-success-800 border-success-200';
      default: return 'bg-neutral-100 text-neutral-800 border-neutral-200';
    }
  };

  // Generate summary text
  const getSummary = () => {
    if (safeGoals.length === 0) {
      return 'No goals defined yet';
    }
    if (safeGoals.length === 1) {
      const text = safeGoals[0].text;
      return `1 goal: ${text.length > 60 ? text.substring(0, 60) + '...' : text}`;
    }
    const goalTexts = safeGoals.map(g => g.text).join(', ');
    const summary = `${safeGoals.length} goals: ${goalTexts}`;
    // Truncate to ~80 characters to keep it as a one-liner
    return summary.length > 80 ? summary.substring(0, 80) + '...' : summary;
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-neutral-200 ${densityClasses.card}`}>
      {/* Header - Clickable to toggle collapse */}
      <div 
        className="border-b border-neutral-200 pb-3 cursor-pointer hover:bg-neutral-50 transition-colors"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            <div className="flex-shrink-0">
              {isCollapsed ? (
                <ChevronDownIcon className="w-5 h-5 text-neutral-600" />
              ) : (
                <ChevronUpIcon className="w-5 h-5 text-neutral-600" />
              )}
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold text-neutral-900 ${densityClasses.heading}`}>
                Client Goals & Objectives
              </h3>
              {isCollapsed ? (
                <p className={`text-sm text-neutral-600 mt-1 ${densityClasses.text}`}>
                  {getSummary()}
                </p>
              ) : (
                <p className={`text-neutral-600 mt-1 text-sm ${densityClasses.text}`}>
                  Define the client's key objectives to influence assessment focus and recommendations.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content - Only show when expanded */}
      {!isCollapsed && (
        <div className="pt-4">
          {/* Add new goal */}
          {isEditable && (
            <div className="mb-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newGoal}
                  onChange={(e) => setNewGoal(e.target.value)}
                  placeholder="Enter a client goal or objective..."
                  className={`flex-1 ${densityClasses.input} border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                  onKeyPress={(e) => e.key === 'Enter' && addGoal()}
                />
                <button
                  onClick={addGoal}
                  disabled={!newGoal.trim()}
                  className={`${densityClasses.button} bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1`}
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Add</span>
                </button>
              </div>
            </div>
          )}

          {/* Goals list */}
          <div className={`space-y-3 ${densityClasses.spacing}`}>
            {safeGoals.length === 0 ? (
              <div className={`text-center py-8 text-neutral-500 ${densityClasses.text}`}>
                No goals defined yet. {isEditable && 'Add goals to customize the assessment focus.'}
              </div>
            ) : (
              safeGoals.map((goal) => (
                <div
                  key={goal.id}
                  className="flex items-start space-x-3 p-3 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
                >
                  <div className="flex-1">
                    <p className={`text-neutral-900 ${densityClasses.text}`}>
                      {goal.text}
                    </p>
                    {isEditable && (
                      <div className="mt-2 flex items-center space-x-2">
                        <span className={`text-xs ${densityClasses.text}`}>Priority:</span>
                        <select
                          value={goal.priority}
                          onChange={(e) => updateGoalPriority(goal.id, e.target.value as 'high' | 'medium' | 'low')}
                          className={`text-xs px-2 py-1 rounded border ${getPriorityColor(goal.priority)} focus:outline-none focus:ring-1 focus:ring-primary-500`}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    )}
                  </div>
                  {isEditable && (
                    <button
                      onClick={() => removeGoal(goal.id)}
                      className="text-neutral-400 hover:text-error-500 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientGoalsSection;
